import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { apiGet, apiPut } from '@/lib/http/client';

export const ACTION_ROUTING_POOL_QUERY_KEY = (
  orgId: number | null | undefined
) => ['actionRoutingPool', orgId] as const;

export interface ActionRoutingPool {
  userIds: number[];
  isLoading: boolean;
  isError: boolean;
  save: (userIds: number[]) => Promise<void>;
  isSaving: boolean;
}

const EMPTY_USER_IDS: readonly number[] = Object.freeze([]);

// Scope: admin-gated action-routing-settings endpoint only; not a generic routing-pool abstraction.
export function useActionRoutingPool(
  orgId: number | null | undefined,
  options?: { enabled?: boolean }
): ActionRoutingPool {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ACTION_ROUTING_POOL_QUERY_KEY(orgId),
    queryFn: async (): Promise<number[]> => {
      const data = await apiGet<{ user_ids: number[] }>(
        `/api/orgs/${orgId}/action-routing-settings/`
      );
      return data.user_ids;
    },
    enabled: !!orgId && (options?.enabled ?? true),
  });

  const mutation = useMutation({
    mutationFn: async (userIds: number[]) => {
      const data = await apiPut<{ user_ids: number[] }>(
        `/api/orgs/${orgId}/action-routing-settings/`,
        { user_ids: userIds }
      );
      return { userIds: data.user_ids, orgId };
    },
    onSuccess: data => {
      queryClient.setQueryData(
        ACTION_ROUTING_POOL_QUERY_KEY(data.orgId),
        data.userIds
      );
    },
  });

  const { mutateAsync } = mutation;
  const save = useCallback(
    async (userIds: number[]) => {
      await mutateAsync(userIds);
    },
    [mutateAsync]
  );

  return useMemo(
    () => ({
      userIds: query.data ?? (EMPTY_USER_IDS as number[]),
      isLoading: query.isLoading,
      isError: query.isError,
      save,
      isSaving: mutation.isPending,
    }),
    [query.data, query.isLoading, query.isError, save, mutation.isPending]
  );
}
